import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, Flame, Sparkles, Instagram } from "lucide-react";
import SocialEmbed from "../components/SocialEmbed";
import LatestDrop from "../components/LatestDrop";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8ce62d96-f600-4cc0-a5c9-9697f10f5a02/images/ab11d55c54edcb1b29cb5a5a7cecbc20b3bf8de726b983eb4bfd895947c43d5e.png";
const PRODUCT = "https://static.prod-images.emergentagent.com/jobs/8ce62d96-f600-4cc0-a5c9-9697f10f5a02/images/9299373664d2bc26dbfa3db427d6e1a388c0ca9ed797e7b4cf09dfa86a2ec286.png";
const SPLAT = "https://static.prod-images.emergentagent.com/jobs/8ce62d96-f600-4cc0-a5c9-9697f10f5a02/images/dcdfeeb51fb24dbbf5f209a671399dfd56317b9a5a7646f041b8353654721047.png";
const SPRAY_TEX = "https://images.unsplash.com/photo-1536639335969-19c9bfc0a4c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const STREET = "/london-graffiti.jpg";
const NEON = "https://images.unsplash.com/photo-1565878025229-b4029f4f7038?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100";
const MLD_LOGO = "/mld-logo.png";

const MLD_HEART = "https://customer-assets.emergentagent.com/job_miss-lolly-designs/artifacts/22257des_IMG_6302.jpeg";
const KIDS_JACKET = "https://customer-assets.emergentagent.com/job_miss-lolly-designs/artifacts/tbx4j2t8_IMG_4157.jpeg";
const COLLAGE_JACKET = "https://customer-assets.emergentagent.com/job_miss-lolly-designs/artifacts/opf801d8_IMG_2658.jpeg";

const TICKER_WORDS = ["DENIM CULT", "RHINESTONE BLING", "VINYL HEAT", "MADE BY HAND", "LONDON STREET", "WEARABLE ART", "MLD"];

const testimonials = [
  { name: "SASKIA, 11", quote: "Made a denim jacket I actually wear to school. My friends keep asking who designed it." },
  { name: "HANNAH — MUM OF TWO", quote: "Forget high-street craft classes. The girls came home glowing — full of new ideas." },
  { name: "PRIYA, 14", quote: "Real heat presses, real vinyl, real fashion tools. Finally a workshop that doesn't treat us like babies." },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-ink">
      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
        </div>

        <div className="relative container-x pt-16 pb-24 md:pt-24 md:pb-36 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 relative z-10">
            <motion.span
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="tag-rose" data-testid="hero-badge"
            >
              ★ MLD · CUSTOM DENIM CULT
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="hero-h mt-7 text-[15vw] md:text-[10vw] lg:text-[9.5rem] text-white"
            >
              Wear it like
              <span className="block text-rose">you mean it.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="mt-8 max-w-xl text-lg text-white/80 leading-relaxed"
            >
              Custom denim. Rhinestone bling. Vinyl heat. Miss Lolly Designs is a London
              street-luxury studio turning t-shirts, totes and jackets into wearable art —
              for ages 8 through grown-up.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
              className="mt-10 flex flex-wrap gap-5"
            >
              <Link to="/workshops" data-testid="hero-book-btn" className="btn-accent">
                Book a workshop <ArrowUpRight size={16} />
              </Link>
              <Link to="/gallery" data-testid="hero-gallery-btn" className="btn-secondary">
                See the drops
              </Link>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 max-w-md gap-6">
              {[
                { n: "300+", l: "Pieces drop'd" },
                { n: "8 → 100", l: "All ages" },
                { n: "5★", l: "Parent-rated" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-4xl text-gold">{s.n}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="lg:col-span-4 relative z-10"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gold/20 blur-3xl" />
              <img src={PRODUCT} alt="Custom denim with rhinestones" className="relative w-full aspect-[3/4] object-cover border-2 border-white" />
              <div className="absolute -top-3 -right-3 tag-sticker !bg-rose !text-white animate-wobble">NEW DROP</div>
              <div className="absolute -bottom-4 -left-4 tag-gold">RHINESTONE LAB</div>
              {/* MLD logo stamp */}
              <motion.img
                initial={{ opacity: 0, rotate: -30, scale: 0.5 }}
                animate={{ opacity: 1, rotate: -12, scale: 1 }}
                transition={{ delay: 1.0, type: "spring", stiffness: 120 }}
                src={MLD_LOGO}
                alt="MLD seal"
                className="absolute -bottom-10 -right-6 w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_8px_20px_rgba(255,42,112,0.6)]"
              />
            </div>
          </motion.div>
        </div>

        <div className="stripe-divider" />
      </section>

      {/* MARQUEE TICKER */}
      <section className="relative bg-rose border-y-2 border-white overflow-hidden py-4">
        <div className="ticker-row whitespace-nowrap">
          {[0, 1].map((j) => (
            <div key={`ticker-row-${j}`} className="flex shrink-0 gap-10 px-6">
              {TICKER_WORDS.map((w) => (
                <span key={`${j}-${w}`} className="font-display text-3xl text-white tracking-tight uppercase">
                  {w} <span className="text-gold mx-3">★</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* LATEST DROP — admin-managed */}
      <LatestDrop />

      {/* SHOWREEL — "What we make" */}
      <section className="section-y relative">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-5 mb-12">
            <div>
              <p className="overline">What we make</p>
              <h2 className="mt-3 font-display text-5xl md:text-7xl text-white">Three ways<br /><span className="text-rose">to flex.</span></h2>
            </div>
            <Link to="/gallery" className="btn-secondary text-xs">See it all</Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { img: KIDS_JACKET, label: "DENIM x SANRIO", tag: "CUSTOMS" },
              { img: COLLAGE_JACKET, label: "LYRIC COLLAGE COAT", tag: "ONE-OFF" },
              { img: MLD_HEART, label: "MLD HEART HTV", tag: "SIGNATURE" },
            ].map((p, i) => (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`showreel-${i}`}
                className="relative group"
              >
                <div className="relative aspect-[3/4] overflow-hidden border-2 border-white">
                  <img src={p.img} alt={p.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors" />
                </div>
                <div className="absolute -top-3 left-4 tag-sticker">{p.tag}</div>
                <p className="mt-4 font-display text-2xl text-white">{p.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="section-y bg-coal border-y-2 border-white/10 relative overflow-hidden">
        <img src={SPRAY_TEX} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-screen" />
        <div className="container-x relative z-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">Philosophy</p>
            <h2 className="mt-4 font-display text-5xl md:text-7xl text-white">Made loud.<br /><span className="text-gold">Made yours.</span></h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { i: Flame, t: "Real fashion tools", d: "Heat presses. Vinyl cutters. Rhinestone applicators. The exact kit indie labels run on." },
              { i: Sparkles, t: "Bling on bling", d: "Rhinestones, gold studs, HTV graphics. Layered until it screams personality." },
              { i: Star, t: "Confidence first", d: "Not perfection. We treat young makers like designers — because that's what they become." },
              { i: ArrowUpRight, t: "Wearable art", d: "Walk out in something nobody else owns. That's the whole point." },
            ].map((x, i) => (
              <div key={x.t} className="card-brutal" data-testid={`feature-${i}`}>
                <x.i className="text-rose" size={26} />
                <h3 className="mt-5 font-display text-2xl text-white">{x.t}</h3>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASE */}
      <section className="section-y">
        <div className="container-x">
          {/* Full-width London graffiti street banner */}
          <div className="relative w-full overflow-hidden border-2 border-white">
            <img
              src={STREET}
              alt="London Shoreditch graffiti alley"
              className="w-full h-[280px] md:h-[420px] lg:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent pointer-events-none" />
            <div className="absolute bottom-4 right-4 tag-rose">MLD STUDIO · LONDON</div>
          </div>

          {/* Text block below banner */}
          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <p className="overline">Behind the brand</p>
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-display text-5xl md:text-7xl text-white">I'm <span className="text-rose">Lisa.</span><br />Founder of MLD.</h2>
              <p className="mt-7 text-white/80 text-lg leading-relaxed max-w-3xl">
                MLD started from something deeply personal and grew into a creative space
                where fashion, personalisation and community come together. Custom denim,
                HTV vinyl, rhinestones — the techniques real labels use, taught in a fun,
                inclusive studio where everyone walks out wearing something they made.
              </p>
              <Link to="/about" data-testid="about-cta" className="mt-8 inline-flex btn-secondary text-xs">Read the story <ArrowUpRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL */}
      <SocialEmbed />

      {/* TESTIMONIALS + REVIEW CTA */}
      <section id="reviews" className="section-y">
        <div className="container-x">
          <p className="overline text-center">Real talk</p>
          <h2 className="mt-3 font-display text-5xl md:text-7xl text-center text-white">Words from <span className="text-gold">the cult.</span></h2>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div key={t.name}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="card-brutal" data-testid={`testimonial-${i}`}>
                <div className="text-rose font-display text-6xl leading-none">"</div>
                <p className="mt-2 text-white/85 leading-relaxed">{t.quote}</p>
                <p className="mt-6 text-[11px] tracking-[0.25em] uppercase text-gold font-bold">{t.name}</p>
              </motion.div>
            ))}
          </div>

          {/* Leave a review panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-16 relative border-2 border-rose bg-coal p-8 md:p-12 text-center overflow-hidden"
            style={{ boxShadow: "8px 8px 0 #FF2A70" }}
            data-testid="leave-review-panel"
          >
            <p className="overline">Loved your workshop?</p>
            <h3 className="mt-4 font-display text-4xl md:text-6xl text-white">
              Share <span className="text-rose">your story.</span>
            </h3>
            <p className="mt-5 max-w-xl mx-auto text-white/75 text-base leading-relaxed">
              Tag <span className="text-gold font-bold">@misslollydesigns</span> on Instagram with your finished piece,
              or send us a written review — we feature the best ones on the wall.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <a
                href="https://www.instagram.com/misslollydesigns"
                target="_blank"
                rel="noreferrer"
                data-testid="review-ig-tag-btn"
                className="btn-accent text-xs"
              >
                <Instagram size={14} /> Tag @misslollydesigns
              </a>
              <a
                href="mailto:misslollydesigns@gmail.com?subject=Workshop%20review"
                data-testid="review-contact-btn"
                className="btn-secondary text-xs"
              >
                Send a written review <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-y">
        <div className="container-x">
          <div className="relative bg-rose border-2 border-white p-10 md:p-20 overflow-hidden">
            <img src={SPLAT} alt="" className="absolute -right-20 -top-20 w-[500px] opacity-40 mix-blend-screen pointer-events-none" />
            <img src={NEON} alt="" className="absolute -left-20 -bottom-20 w-[400px] opacity-25 mix-blend-overlay pointer-events-none" />
            <div className="relative z-10 max-w-2xl">
              <p className="overline !text-white">Studio open</p>
              <h2 className="mt-4 font-display text-5xl md:text-7xl text-white">Drop into<br /><span className="text-ink">the studio.</span></h2>
              <p className="mt-6 text-white/85 text-lg max-w-md">
                Workshops fill quick — small groups, generous hours. £10 deposit per attendee secures the spot.
              </p>
              <Link to="/workshops" data-testid="bottom-cta-btn" className="mt-8 inline-flex items-center gap-2 bg-ink text-white border-2 border-white px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm hover:bg-gold hover:text-ink transition" style={{ boxShadow: "6px 6px 0 #FFFFFF" }}>
                Book a workshop <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
